// Taşeron portalı — kişisel profil (Self) sayfası. api-contract §18.2 (`/supplier/me`).
//
// Üç bölüm: (1) salt-okunur bilgi kartı (ad-soyad + e-posta/telefon + aktif firma), (2) ad-soyad
// düzenleme formu (`PUT /supplier/me` → `useSupplierMe` cache'i tazelenir → header de güncellenir),
// (3) parola değiştirme formu (`POST /supplier/me/change-password`). Profil `AllAuthorized`
// olduğundan yetki sondası (Gate) GEREKMEZ — RequireAuth altında her kimlik-doğrulanmış personel
// erişir. Self-profil anahtarı aktif firmayı içerir → switcher ile firma değişince yenilenir.
//
// **Parola güvenliği (§18).** Alanlar `type="password"` + uygun `autoComplete`; değerler yalnız
// RHF state'te tutulur, gövdede POST edilir (URL/log'a ASLA girmez), başarıda form temizlenir.
// Hata kodları alan bazında: `WRONG_PASSWORD` → mevcut parola, `PASSWORD_POLICY_ERROR` → yeni parola.

import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Breadcrumb,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Field,
  Input,
  PageHeader,
  Spinner,
  isApiError,
  profileApi,
  resolveApiErrorMessage,
  useAuthStore,
} from '@isgportal/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { meKeys } from '../lib/meKeys';
import { useSupplierMe } from '../lib/useSupplierMe';

export function ProfilePage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const companyId = useAuthStore((s) => s.principal?.companyId);
  const supplierUserId = useAuthStore((s) => s.principal?.supplierUserId);
  const { me, isPending } = useSupplierMe();

  const [profileMessage, setProfileMessage] = useState<string | null>(null);
  const [passwordMessage, setPasswordMessage] = useState<string | null>(null);
  const [passwordGeneralError, setPasswordGeneralError] = useState<string | null>(null);

  // --- Ad-soyad düzenleme (PUT /supplier/me) -----------------------------------------
  const profileSchema = useMemo(
    () =>
      z.object({
        firstName: z.string().trim().min(1, t('profile.validation.firstNameRequired')),
        lastName: z.string().trim().min(1, t('profile.validation.lastNameRequired')),
      }),
    [t],
  );
  type ProfileValues = z.infer<typeof profileSchema>;

  const {
    register: registerProfile,
    handleSubmit: handleProfileSubmit,
    formState: profileState,
  } = useForm<ProfileValues>({
    resolver: zodResolver(profileSchema),
    values: {
      firstName: me?.firstName ?? '',
      lastName: me?.lastName ?? '',
    },
  });

  const profileMutation = useMutation({
    mutationFn: (values: ProfileValues) =>
      profileApi.updateSupplierMe({
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
      }),
    onSuccess: (updated) => {
      // Güncellenmiş DTO'yu aktif-firma anahtarına yaz — header (aynı anahtar) anında tazelenir.
      queryClient.setQueryData(meKeys.me(companyId, supplierUserId), updated);
      setProfileMessage(t('profile.updated'));
    },
  });

  // --- Parola değiştirme (POST /supplier/me/change-password) --------------------------
  const policyMessage = t('validation.passwordPolicy');
  const passwordSchema = useMemo(
    () =>
      z
        .object({
          currentPassword: z.string().min(1, t('profile.validation.currentPasswordRequired')),
          newPassword: z
            .string()
            .min(8, policyMessage)
            .regex(/[A-Za-z]/, policyMessage)
            .regex(/\d/, policyMessage),
          confirmPassword: z.string().min(1, t('profile.validation.confirmRequired')),
        })
        .refine((data) => data.newPassword === data.confirmPassword, {
          path: ['confirmPassword'],
          message: t('profile.validation.passwordMismatch'),
        }),
    [t, policyMessage],
  );
  type PasswordValues = z.infer<typeof passwordSchema>;

  const {
    register: registerPassword,
    handleSubmit: handlePasswordSubmit,
    reset: resetPassword,
    setError: setPasswordFieldError,
    formState: passwordState,
  } = useForm<PasswordValues>({
    resolver: zodResolver(passwordSchema),
    defaultValues: { currentPassword: '', newPassword: '', confirmPassword: '' },
  });

  const passwordMutation = useMutation({
    mutationFn: (values: PasswordValues) =>
      profileApi.changeSupplierPassword({
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
      }),
    onSuccess: () => {
      // Başarıda form temizlenir (parola değeri state'te kalmaz).
      resetPassword({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setPasswordMessage(t('profile.password.updated'));
    },
    onError: (error) => {
      // Bilinen hata kodları alan bazında; diğerleri genel uyarıda (§18).
      if (isApiError(error)) {
        if (error.errorCode === 'WRONG_PASSWORD') {
          setPasswordFieldError('currentPassword', { message: t('profile.password.wrongCurrent') });
          return;
        }
        if (error.errorCode === 'PASSWORD_POLICY_ERROR') {
          setPasswordFieldError('newPassword', { message: policyMessage });
          return;
        }
      }
      setPasswordGeneralError(resolveApiErrorMessage(error, t));
    },
  });

  return (
    <div className="flex flex-col gap-6">
      <Breadcrumb items={[{ label: t('profile.title') }]} />
      <PageHeader title={t('profile.title')} helperText={t('profile.subtitle')} />

      {isPending ? (
        <div className="flex min-h-[30vh] items-center justify-center">
          <Spinner label={t('profile.loading')} />
        </div>
      ) : !me ? (
        <Alert tone="danger">{t('error.generic')}</Alert>
      ) : (
        <>
          {/* (1) Salt-okunur bilgi kartı */}
          <Card>
            <CardHeader>
              <CardTitle>{t('profile.info.title')}</CardTitle>
              <CardDescription>{t('profile.info.subtitle')}</CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col gap-3 carbon:pt-3">
              <InfoRow
                label={t('profile.field.fullName')}
                value={`${me.firstName} ${me.lastName}`.trim()}
              />
              <InfoRow label={t('profile.field.emailOrPhone')} value={me.emailOrPhone} />
              <InfoRow label={t('profile.field.company')} value={me.companyName ?? '—'} />
            </CardContent>
          </Card>

          {/* (2) Ad-soyad düzenleme */}
          <Card>
            <CardHeader>
              <CardTitle>{t('profile.edit.title')}</CardTitle>
              <CardDescription>{t('profile.edit.subtitle')}</CardDescription>
            </CardHeader>
            <CardContent className="carbon:pt-3">
              {profileMessage ? (
                <Alert tone="success" className="mb-4">
                  {profileMessage}
                </Alert>
              ) : null}
              <form
                className="flex flex-col gap-4"
                onSubmit={handleProfileSubmit((values) => {
                  setProfileMessage(null);
                  profileMutation.mutate(values);
                })}
                noValidate
              >
                {profileMutation.isError ? (
                  <Alert tone="danger">{resolveApiErrorMessage(profileMutation.error, t)}</Alert>
                ) : null}

                <div className="grid gap-4 sm:grid-cols-2">
                  <Field
                    htmlFor="profile-firstName"
                    label={t('profile.field.firstName')}
                    error={profileState.errors.firstName?.message}
                    required
                  >
                    <Input
                      id="profile-firstName"
                      autoComplete="given-name"
                      invalid={Boolean(profileState.errors.firstName)}
                      {...registerProfile('firstName')}
                    />
                  </Field>
                  <Field
                    htmlFor="profile-lastName"
                    label={t('profile.field.lastName')}
                    error={profileState.errors.lastName?.message}
                    required
                  >
                    <Input
                      id="profile-lastName"
                      autoComplete="family-name"
                      invalid={Boolean(profileState.errors.lastName)}
                      {...registerProfile('lastName')}
                    />
                  </Field>
                </div>

                <div className="flex items-center justify-end pt-1">
                  <Button type="submit" loading={profileMutation.isPending}>
                    {t('profile.edit.save')}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>

          {/* (3) Parola değiştirme */}
          <Card>
            <CardHeader>
              <CardTitle>{t('profile.password.title')}</CardTitle>
              <CardDescription>{t('profile.password.subtitle')}</CardDescription>
            </CardHeader>
            <CardContent className="carbon:pt-3">
              {passwordMessage ? (
                <Alert tone="success" className="mb-4">
                  {passwordMessage}
                </Alert>
              ) : null}
              <form
                className="flex flex-col gap-4"
                onSubmit={handlePasswordSubmit((values) => {
                  setPasswordMessage(null);
                  setPasswordGeneralError(null);
                  passwordMutation.mutate(values);
                })}
                noValidate
              >
                {passwordGeneralError ? <Alert tone="danger">{passwordGeneralError}</Alert> : null}

                <Field
                  htmlFor="profile-currentPassword"
                  label={t('profile.password.current')}
                  error={passwordState.errors.currentPassword?.message}
                  required
                >
                  <Input
                    id="profile-currentPassword"
                    type="password"
                    autoComplete="current-password"
                    invalid={Boolean(passwordState.errors.currentPassword)}
                    {...registerPassword('currentPassword')}
                  />
                </Field>

                <Field
                  htmlFor="profile-newPassword"
                  label={t('profile.password.new')}
                  hint={t('profile.password.hint')}
                  error={passwordState.errors.newPassword?.message}
                  required
                >
                  <Input
                    id="profile-newPassword"
                    type="password"
                    autoComplete="new-password"
                    invalid={Boolean(passwordState.errors.newPassword)}
                    {...registerPassword('newPassword')}
                  />
                </Field>

                <Field
                  htmlFor="profile-confirmPassword"
                  label={t('profile.password.confirm')}
                  error={passwordState.errors.confirmPassword?.message}
                  required
                >
                  <Input
                    id="profile-confirmPassword"
                    type="password"
                    autoComplete="new-password"
                    invalid={Boolean(passwordState.errors.confirmPassword)}
                    {...registerPassword('confirmPassword')}
                  />
                </Field>

                <div className="flex items-center justify-end pt-1">
                  <Button type="submit" variant="secondary" loading={passwordMutation.isPending}>
                    {t('profile.password.save')}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

/** Salt-okunur bilgi satırı (etiket + değer). */
function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-0.5 sm:flex-row sm:items-center sm:gap-3">
      <span className="w-40 shrink-0 text-sm text-muted">{label}</span>
      <span className="text-sm font-medium text-fg">{value}</span>
    </div>
  );
}
